import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Coffee,
  Cookie,
  Zap,
  Store,
  CreditCard,
  BarChart3,
  Wrench,
  Clock,
  Star,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import LeadForm from "@/components/LeadForm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_53c9bebf-1df6-461a-ae92-22968598c10c/artifacts/xusxetuj_LOGO.png";

const Home = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [testimonialsRes, faqsRes] = await Promise.all([
          axios.get(`${API}/testimonials`),
          axios.get(`${API}/faqs`),
        ]);
        setTestimonials(testimonialsRes.data);
        setFaqs(faqsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const services = [
    {
      icon: Coffee,
      title: "Drink Machines",
      description: "Cold beverages from sodas to energy drinks",
      path: "/services/drink-machines",
    },
    {
      icon: Cookie,
      title: "Snack Machines",
      description: "Chips, candy, and healthy options",
      path: "/services/snack-machines",
    },
    {
      icon: Zap,
      title: "Combo Machines",
      description: "The best of both worlds",
      path: "/services/combo-machines",
    },
    {
      icon: Store,
      title: "Micro Markets",
      description: "Mini convenience store experience",
      path: "/services/micro-markets",
    },
  ];

  const features = [
    {
      icon: CreditCard,
      title: "Cashless Payments",
      description: "Apple Pay, Google Pay, and card payments supported",
    },
    {
      icon: BarChart3,
      title: "Smart Stocking",
      description: "Products selected based on real consumption data",
    },
    {
      icon: Wrench,
      title: "Full Service",
      description: "We handle installation, stocking, and maintenance",
    },
    {
      icon: Clock,
      title: "24/7 Monitoring",
      description: "Remote monitoring ensures maximum uptime",
    },
  ];

  return (
    <div className="overflow-hidden" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden" data-testid="hero-section">
        {/* Background Decorations */}
        <div className="absolute top-20 left-10 w-8 h-8 bg-gold rotate-12 opacity-60" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
        <div className="absolute top-40 right-20 w-6 h-6 bg-coral rotate-45 opacity-60" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
        <div className="absolute bottom-20 left-1/4 w-10 h-10 bg-gold opacity-40" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="animate-fade-in-up">
              <span className="inline-block bg-gold text-navy font-mono text-sm px-4 py-2 rounded-full border-2 border-navy mb-6">
                ZERO COST AMENITY
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-navy leading-tight mb-6">
                Refreshment That{" "}
                <span className="text-coral">Elevates</span> Your Space
              </h1>
              <p className="text-lg text-navy/80 mb-8 max-w-xl">
                521 Vending delivers curated snacks and drinks to your workplace,
                apartment, or commercial property. Professional, reliable, and
                completely hassle-free.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary" data-testid="hero-cta-primary">
                  Get Started Free
                </Link>
                <Link to="/how-it-works" className="btn-secondary" data-testid="hero-cta-secondary">
                  See How It Works
                </Link>
              </div>
            </div>

            {/* Right Content - Logo/Image */}
            <div className="relative flex justify-center lg:justify-end animate-fade-in-up animate-delay-200">
              <div className="relative">
                <div className="absolute inset-0 bg-coral rounded-full transform scale-110 -z-10" />
                <img
                  src={LOGO_URL}
                  alt="521 Vending"
                  className="w-64 h-64 md:w-80 md:h-80 object-contain relative z-10"
                  data-testid="hero-logo"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section className="bg-navy py-4 overflow-hidden" data-testid="marquee-section">
        <div className="marquee-container">
          <div className="marquee-content">
            <span className="text-cream font-mono uppercase tracking-widest text-sm">
              FRESH SNACKS • COLD DRINKS • HAPPY TEAMS • ZERO COST • FRESH SNACKS • COLD DRINKS • HAPPY TEAMS • ZERO COST • FRESH SNACKS • COLD DRINKS • HAPPY TEAMS • ZERO COST • FRESH SNACKS • COLD DRINKS • HAPPY TEAMS • ZERO COST •
            </span>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-32" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-cream text-navy font-mono text-sm px-4 py-2 rounded-full border-2 border-navy mb-4">
              OUR SOLUTIONS
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-navy mb-4">
              Vending Solutions for Every Space
            </h2>
            <p className="text-navy/70 max-w-2xl mx-auto">
              From traditional vending to full micro markets, we have the perfect
              refreshment solution for your property.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <Link
                key={service.title}
                to={service.path}
                className="card-retro group cursor-pointer"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`service-card-${service.title.toLowerCase().replace(" ", "-")}`}
              >
                <div className="bg-gold/30 w-16 h-16 rounded-xl flex items-center justify-center mb-4 group-hover:bg-coral/30 transition-colors">
                  <service.icon className="h-8 w-8 text-navy" />
                </div>
                <h3 className="font-heading text-xl text-navy mb-2">
                  {service.title}
                </h3>
                <p className="text-navy/70 text-sm mb-4">{service.description}</p>
                <span className="flex items-center text-coral font-bold text-sm group-hover:gap-2 transition-all">
                  Learn More <ChevronRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32 bg-navy" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-gold text-navy font-mono text-sm px-4 py-2 rounded-full border-2 border-navy mb-4">
              WHY 521 VENDING
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-cream mb-4">
              Modern Amenities, Zero Hassle
            </h2>
            <p className="text-cream/70 max-w-2xl mx-auto">
              We take care of everything so you can focus on what matters most.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="bg-cream/10 backdrop-blur rounded-2xl p-8 border-2 border-cream/20 hover:border-coral transition-colors"
                data-testid={`feature-${feature.title.toLowerCase().replace(" ", "-")}`}
              >
                <div className="bg-coral w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="h-7 w-7 text-cream" />
                </div>
                <h3 className="font-heading text-xl text-cream mb-2">
                  {feature.title}
                </h3>
                <p className="text-cream/70 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 md:py-32" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-cream text-navy font-mono text-sm px-4 py-2 rounded-full border-2 border-navy mb-4">
              TESTIMONIALS
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-navy mb-4">
              What Our Partners Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="card-retro"
                data-testid={`testimonial-${testimonial.id}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-gold text-gold" />
                  ))}
                </div>
                <p className="text-navy/80 mb-6 italic">"{testimonial.content}"</p>
                <div className="border-t-2 border-navy/10 pt-4">
                  <p className="font-bold text-navy">{testimonial.name}</p>
                  <p className="text-sm text-navy/60">
                    {testimonial.role}, {testimonial.company}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-32 bg-gold/20" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-cream text-navy font-mono text-sm px-4 py-2 rounded-full border-2 border-navy mb-4">
              FAQ
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-navy mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={faq.id}
                className="bg-white border-2 border-navy rounded-xl overflow-hidden"
                data-testid={`faq-${faq.id}`}
              >
                <AccordionTrigger className="px-6 py-4 font-bold text-navy hover:text-coral hover:no-underline text-left">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-navy/70">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card-retro bg-coral border-navy text-center">
            <h2 className="font-heading text-3xl md:text-4xl text-cream mb-4">
              Ready to Elevate Your Space?
            </h2>
            <p className="text-cream/90 mb-8 max-w-xl mx-auto">
              Get a free consultation and see how 521 Vending can add value to
              your property at zero cost.
            </p>
            <Link to="/contact" className="btn-secondary inline-block" data-testid="cta-button">
              Get Your Free Quote
            </Link>
          </div>
        </div>
      </section>

      {/* Lead Form Section */}
      <section className="py-20 md:py-32 bg-navy" data-testid="lead-form-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-cream rounded-2xl border-2 border-gold p-8 md:p-12 shadow-[8px_8px_0px_0px_#F2CC8F]">
            <div className="text-center mb-8">
              <h2 className="font-heading text-3xl text-navy mb-2">
                Request Your Free Machine
              </h2>
              <p className="text-navy/70">
                Fill out the form below and we'll be in touch within 24 hours.
              </p>
            </div>
            <LeadForm variant="compact" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
